<template>
  <section :class="[$style.parts_container, { [$style.reverse]: textOrientation === 'right' }]">
    <div :class="$style.text_area">
      <div :class="$style.text_content">
        <h3
          :class="[$style.title, fontSize, fontWeight, { 'page-parts-pc': spTitle ? true : false }]"
          v-html="sanitizeHtmlContent(title)"
        />
        <h3
          v-if="spTitle"
          class="page-parts-sp"
          :class="[$style.title, fontSize, fontWeight]"
          v-html="sanitizeHtmlContent(spTitle)"
        />
        <p
          class="page-parts-pc"
          :class="[$style.description, descFontSize]"
          v-html="sanitizeHtmlContent(description)"
        />
        <p
          v-if="spDescription"
          class="page-parts-sp"
          :class="[$style.description, descFontSize]"
          v-html="sanitizeHtmlContent(spDescription)"
        />
        <AtomsConsumerLink v-if="linkUrl" :url="linkUrl" :class="$style.link">
          <MoleculesConsumerButtonWithArrowRight variant="secondary">{{
            linkText
          }}</MoleculesConsumerButtonWithArrowRight>
        </AtomsConsumerLink>
      </div>
    </div>
    <div :class="$style.image_area">
      <AtomsConsumerImage
        :src="imgUrl"
        :alt="imgUrlCaption"
        :width="windowWidth > 858 ? 858 : windowWidth"
        :height="509"
        :class="{ 'page-parts-pc': spImgUrl }"
      />
      <AtomsConsumerImage
        v-if="spImgUrl"
        :src="spImgUrl"
        :alt="spImgUrlCaption"
        :width="windowWidth > 858 ? 858 : windowWidth"
        :height="509"
        class="page-parts-sp"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { sanitizeHtmlContent } from "@tential/ec-gql-schema/utils/functions/sanitize"

type Props = {
  title: string
  spTitle?: string
  description?: string
  spDescription?: string
  fontSize: string[]
  descFontSize: string[]
  fontWeight: string
  imgUrl: string
  spImgUrl?: string | null
  textOrientation?: string
  imgUrlCaption?: string
  spImgUrlCaption?: string
  linkUrl?: string
  linkText?: string
  textBackgroundColor: string
  spImgWindowFull?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  title: "",
  spTitle: "",
  description: "",
  spDescription: "",
  fontSize: () => [],
  descFontSize: () => [],
  fontWeight: "",
  imgUrl: "",
  spImgUrl: null,
  textOrientation: "",
  imgUrlCaption: "",
  spImgUrlCaption: "",
  linkText: "詳しくはこちら",
  linkUrl: "",
  textBackgroundColor: "",
  spImgWindowFull: false,
})

const textAreaBackgroundColor = computed(() => {
  return props.textBackgroundColor ?? "white"
})

const spImagePadding = computed(() => {
  return props.spImgWindowFull ? "0" : "0 10%"
})

const { width: windowWidth } = useWindowSize()
</script>

<style scoped module lang="scss">
.parts_container {
  @include full-container-lg;
  display: flex;
  flex-direction: column;

  @include lg {
    flex-direction: row;
    justify-content: space-between;
    &.reverse {
      flex-direction: row-reverse;
    }
  }

  .text_area {
    margin: auto 0;
    width: 100%;
    order: 2;
    padding: 2.5rem 1.875rem;
    background-color: v-bind(textAreaBackgroundColor);

    @include lg {
      margin: 0;
      width: 40%;
      order: 0;
      padding: 2.5rem 4rem;
    }

    .text_content {
      height: 100%;
      display: flex;
      flex-direction: column;
      gap: 1rem;

      @include lg {
        justify-content: center;
      }

      .title {
        color: $primary;
        font-size: $font-size-22;
        white-space: pre-wrap;

        @include lg {
          font-size: $font-size-32;
        }
      }
      .description {
        color: $black-90;
        font-size: $font-size-12;
        white-space: pre-wrap;

        @include lg {
          font-size: $font-size-15;
        }
      }
      .link {
        width: fit-content;
      }
    }
  }

  .image_area {
    order: 1;
    width: 100%;
    aspect-ratio: unset;
    padding: v-bind(spImagePadding);

    @include lg {
      width: 60%;
      aspect-ratio: 858 / 509;
      padding: 0;
    }

    > img {
      height: 100%;
      width: 100%;
      object-fit: cover;
    }
  }
}
</style>
